import * as types from "../types";
import apiService from "../../services/api";

import { Commit, Dispatch } from "vuex";

const defaultFormValues = {
  // step 1
  eligible: null,

  // step 2
  ownRentSpace: null,
  ownRentSpaceType: "",
  ownRentSpaceComment: "",
  fullTimeCount: "",
  partTimeCount: "",
  contractCount: "",

  estIncreasedExpenditures: "",
  estLostProfit: "",
  estLostProfitDesc: "",

  reliefSourcePpp: "",
  reliefSourceSba: "",
  reliefSourceCounty: "",
  reliefSourceCity: "",
  reliefSourceUnemployment: "",
  reliefSourceOtherLabel: "",
  reliefSourceOther: "",
  reliefSourceArpaGreene: "",
  reliefSourceArpaMo: "",

  proposedProjectDesc1: "",
  proposedProjectAmount1: "",
  proposedProjectDesc2: "",
  proposedProjectAmount2: "",
  proposedProjectDesc3: "",
  proposedProjectAmount3: "",

  // step 3
  businessName: "",
  dba: "",
  website: "",
  phone: "",
  mailingAddressLine1: "",
  mailingAddressLine2: "",
  mailingAddressCity: "",
  mailingAddressState: "",
  mailingAddressZip: "",
  operationAddressLine1: "",
  operationAddressLine2: "",
  operationAddressCity: "",
  operationAddressState: "",
  operationAddressZip: "",
  addressIsHome: null,
  servicesGoodProvided: "",
  servicesGoodProvidedOther: "",
  inGreeneCounty: null,
  outsideGreeneCounty: null,
  yearEstablished: "",
  monthEstablished: "",
  fein: "",
  hasBusinessLicense: "",
  noWebsiteExplain: "",
  complianceStateLicense: "",
  complianceLicenseNumber: "",
  parentCompany: "",
  parentCompanyName: "",
  pocName: "",
  pocEmail: "",
  pocPhone: "",
  pocTitle: "",
  servicesCategory: "",

  // step 4
  race: "",
  gender: "",
  veteranOwned: null,
  disability: null,
  othersEmployed: null,
  othersRaceWhite: "",
  othersRaceBlack: "",
  othersRaceLatino: "",
  othersRaceNative: "",
  othersRaceAsian: "",
  othersRaceMore: "",
  othersRaceOther: "",
  othersRaceUnsure: "",

  // step 5
  utilizationEconomic: null,
  utilizationHealth: null,
  accordance: "",
  howFundsUsed: "",
  howRecover: "",
  complianceArpa: null,
  complianceSinceStart: null,
  complianceTaxCurrent: null,
  complianceTaxCurrentWhy: "",
  complianceBusinessLicense: "",
  complianceSuspended: null,
  complianceLoanDelinquence: null,

  // step6
  uploadEmployer: "",
  uploadSole: "",
  w9: "",
  attachment1: "",
  attachment2: "",
  attachment3: "",
  eVerifyMou: "",
};
type FormValuesState = typeof defaultFormValues;
type anyFormValues = { [x: string]: number | null | string | boolean };

const defaultMetaValues = {
  id: 0,
  currentStep: 0,
  submitted: false,
  status: "New",
  comments: "",
  userId: 0,
  createdAt: {
    date: "",
    timezone_type: 0,
    timezone: "",
  },
};
type MetaValuesState = typeof defaultMetaValues;

const initialApplicationState = {
  ...defaultMetaValues,
  ...defaultFormValues,
};
export type ApplicationState = MetaValuesState & FormValuesState;

type State = {
  [types.APPLICATIONS_LIST]: MetaValuesState[];
  [types.APPLICATION]: ApplicationState;
  [types.SAVING_FORM]: boolean;
  [types.LOADING_APPLICATION]: boolean;
  [types.SAVING_FORM_ERROR]: boolean | string;
};
const state: State = {
  [types.APPLICATIONS_LIST]: [],
  [types.APPLICATION]: initialApplicationState,
  [types.SAVING_FORM]: false,
  [types.LOADING_APPLICATION]: false,
  [types.SAVING_FORM_ERROR]: false,
};

const getters = {
  [types.CURRENT_STATUS]: (state: State) => {
    return state[types.APPLICATION].status;
  },
  [types.CURRENT_STEP]: (state: State) => {
    return state[types.APPLICATION].currentStep;
  },
  [types.FORM_VALUES]: (state: State): FormValuesState => {
    // pull form values out of the application state
    const formValues = {} as FormValuesState;
    Object.keys(defaultFormValues).forEach(
      // @ts-ignore
      (key) => (formValues[key] = state[types.APPLICATION][key])
    );
    return formValues;
  },
  [types.APPLICATION_SUBMITTED]: (state: State) => {
    return state[types.APPLICATION].submitted;
  },
  [types.APPLICATION]: (state: State) => {
    return state[types.APPLICATION];
  },
  [types.APPLICATIONS_LIST]: (state: State) => {
    return state[types.APPLICATIONS_LIST];
  },
  [types.LOADING_APPLICATION]: (state: State) => {
    return state[types.LOADING_APPLICATION];
  },
  [types.SAVING_FORM]: (state: State) => {
    return state[types.SAVING_FORM];
  },
  [types.SAVING_FORM_ERROR]: (state: State) => {
    return state[types.SAVING_FORM_ERROR];
  },
};

const mutations = {
  [types.MUTATE_APPLICATION]: (state: State, payload: ApplicationState) => {
    state[types.APPLICATION] = payload;
  },
  [types.MUTATE_APPLICATIONS_LIST]: (
    state: State,
    payload: ApplicationState[]
  ) => {
    state[types.APPLICATIONS_LIST] = payload;
  },
  [types.MUTATE_PARTIAL_FORM_VALUES]: (
    state: State,
    payload: anyFormValues
  ) => {
    state[types.APPLICATION] = { ...state[types.APPLICATION], ...payload };
  },
  [types.MUTATE_LOADING_APPLICATION]: (state: State, payload: boolean) => {
    state[types.LOADING_APPLICATION] = payload;
  },
  [types.MUTATE_SAVING_FORM]: (state: State, payload: boolean) => {
    state[types.SAVING_FORM] = payload;
  },
  [types.MUTATE_CURRENT_STEP]: (state: State, payload: number) => {
    state[types.APPLICATION].currentStep = payload;
  },
  [types.MUTATE_SAVING_FORM_ERROR]: (
    state: State,
    payload: boolean | string
  ) => {
    state[types.SAVING_FORM_ERROR] = payload;
  },
  [types.MUTATE_SUBMITTED_FORM]: (state: State) => {
    state[types.APPLICATION].status = "Submitted";
    state[types.APPLICATION].submitted = true;
  },
};

const actions = {
  [types.NEW_APPLICATION]: (
    {
      commit,
      dispatch,
    }: {
      commit: Commit;
      dispatch: Dispatch;
    },
    payload: { userId: number }
  ) => {
    commit(types.MUTATE_LOADING_APPLICATION, true);

    return apiService.newApplication().finally(() => {
      commit(types.MUTATE_LOADING_APPLICATION, false);
      dispatch(types.LIST_USER_APPLICATIONS, payload);
    });
  },

  [types.LOAD_APPLICATION]: (
    { commit }: { commit: Commit },
    payload: { applicationId: number }
  ) => {
    commit(types.MUTATE_LOADING_APPLICATION, true);

    return apiService
      .loadApplication(payload)
      .then((res) => {
        if (!res) return;

        commit(types.MUTATE_APPLICATION, res);
      })
      .finally(() => commit(types.MUTATE_LOADING_APPLICATION, false));
  },

  [types.LIST_USER_APPLICATIONS]: (
    { commit }: { commit: Commit },
    payload: { userId: number }
  ) => {
    commit(types.MUTATE_LOADING_APPLICATION, true);

    return apiService
      .loadUserApplications(payload)
      .then((res) => {
        if (!res) return;

        commit(types.MUTATE_APPLICATIONS_LIST, res);
      })
      .finally(() => commit(types.MUTATE_LOADING_APPLICATION, false));
  },

  // save partial form fields, used per-step
  [types.SAVE_FORM_STEP]: (
    { commit, state }: { commit: Commit; state: State },
    payload: {
      applicationId: number;
      formFields?: anyFormValues;
      newCurStepNum?: number;
    }
  ) => {
    commit(types.MUTATE_SAVING_FORM, true);

    commit(types.MUTATE_PARTIAL_FORM_VALUES, payload.formFields);

    if (!payload.applicationId)
      payload.applicationId = state[types.APPLICATION].id;

    return apiService
      .saveStep(payload)
      .then(() => {
        commit(types.MUTATE_SAVING_FORM_ERROR, false);

        if (
          payload.newCurStepNum &&
          payload.newCurStepNum > state[types.APPLICATION].currentStep
        )
          commit(types.MUTATE_CURRENT_STEP, payload.newCurStepNum);
      })

      .catch((err) => {
        commit(
          types.MUTATE_SAVING_FORM_ERROR,
          err
            ? err
            : "Sorry, something went wrong. Please contact us for assistance."
        );
      })
      .finally(() => {
        commit(types.MUTATE_SAVING_FORM, false);
      });
  },
  [types.SUBMIT_APPLICATION]: ({
    commit,
    state,
  }: {
    commit: Commit;
    state: State;
  }) => {
    commit(types.MUTATE_SAVING_FORM, true);
    apiService
      .submitForm({
        applicationId: state[types.APPLICATION].id,
      })
      .then(() => {
        commit(types.MUTATE_SAVING_FORM_ERROR, false);
        commit(types.MUTATE_SUBMITTED_FORM);
      })

      .catch((err) => {
        commit(
          types.MUTATE_SAVING_FORM_ERROR,
          err
            ? err
            : "Sorry, something went wrong. Please contact us for assistance."
        );
      })
      .finally(() => {
        commit(types.MUTATE_SAVING_FORM, false);
      });
  },
};

export default {
  state,
  mutations,
  actions,
  getters,
};
